import React, { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Skeleton } from '../Skeleton';
import type { EnrichedSignal, StrategyMatchResult } from '../../types/signals';
import StrategyBreakdownRow from './StrategyBreakdownRow';

interface StrategySignalCardProps {
  symbol: string;
  data: EnrichedSignal;
  onRefresh: () => void;
  isLoading?: boolean;
}

function scoreBarColor(score: number): string {
  if (score >= 75) return 'bg-emerald-500';
  if (score >= 50) return 'bg-amber-500';
  return 'bg-slate-500';
}

function DirectionBadge({ direction }: { direction: string }) {
  const styles: Record<string, string> = {
    BUY: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    SELL: 'bg-red-500/20 text-red-400 border-red-500/30',
    HOLD: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  };
  const labels: Record<string, string> = { BUY: '🟢 BUY', SELL: '🔴 SELL', HOLD: '⚫ HOLD' };
  const s = styles[direction] ?? styles.HOLD;
  return (
    <span className={`text-xs px-2 py-0.5 rounded border font-semibold ${s}`}>
      {labels[direction] ?? direction}
    </span>
  );
}

function patternNarrative(_result: StrategyMatchResult, patternName: string): string {
  const narratives: Record<string, string> = {
    BOS: 'Break of Structure confirmed',
    CHoCH: 'Change of Character confirmed',
    FVG: 'Fair Value Gap — price magnet',
    'Zone Retest': 'Demand zone held',
    'Order Block': 'Order block confirmed',
    'Breaker Block': 'Breaker block in range',
    IOFED: 'IOFED pattern active',
    OTE: 'Optimal Trade Entry zone',
    MSS: 'Market Structure Shift detected',
  };
  return narratives[patternName] ?? `${patternName} confirmed`;
}

export const StrategySignalCardSkeleton: React.FC = () => (
  <div className="rounded-xl border border-slate-700/50 p-5 bg-slate-800/30 space-y-4">
    <div className="flex items-center justify-between">
      <Skeleton className="h-3 w-32" />
      <Skeleton className="h-6 w-16" />
    </div>
    <Skeleton className="h-5 w-64" />
    <div className="flex items-center gap-3">
      <Skeleton className="flex-1 h-2 rounded-full" />
      <Skeleton className="h-4 w-8" />
      <Skeleton className="h-5 w-16 rounded" />
      <Skeleton className="h-4 w-24" />
    </div>
    <div className="grid grid-cols-2 gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-start gap-2">
          <Skeleton className="h-4 w-4 rounded" />
          <div className="flex-1 space-y-1">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-32" />
          </div>
        </div>
      ))}
    </div>
    <Skeleton className="h-3 w-28" />
    <Skeleton className="h-16 rounded-lg" />
    <div className="space-y-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-9 rounded-lg" />
      ))}
    </div>
  </div>
);

const VISIBLE_COUNT = 5;

const StrategySignalCard: React.FC<StrategySignalCardProps> = ({
  symbol: _symbol,
  data,
  onRefresh,
  isLoading = false,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  if (isLoading) return <StrategySignalCardSkeleton />;

  const best =
    data.strategy_breakdown.find((r) => r.is_best_match) ?? data.strategy_breakdown[0];

  if (!best) {
    return (
      <div className="rounded-xl border border-slate-700/50 p-5 bg-slate-800/30">
        <p className="text-sm text-slate-500 text-center">No strategy data available.</p>
      </div>
    );
  }

  const visibleRows = showAll
    ? data.strategy_breakdown
    : data.strategy_breakdown.slice(0, VISIBLE_COUNT);
  const hiddenCount = Math.max(0, data.strategy_breakdown.length - VISIBLE_COUNT);

  const toggleRow = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="rounded-xl border border-slate-700/50 p-5 bg-slate-800/30">
      {/* Section 1 — Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-semibold tracking-widest text-amber-400 uppercase">
          🏆 Strategy Signal
        </span>
        <button
          onClick={onRefresh}
          className="flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors"
          aria-label="Refresh strategy breakdown"
        >
          <RefreshCw size={12} className={isLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <p className="text-base font-semibold text-white mb-2">{data.matched_strategy}</p>

      <div className="flex items-center gap-3 mb-1">
        <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${scoreBarColor(data.match_score)}`}
            style={{ width: `${data.match_score}%` }}
          />
        </div>
        <span className="text-sm font-bold text-slate-200 w-10 text-right">
          {data.match_score.toFixed(0)}%
        </span>
        <DirectionBadge direction={data.direction} />
        <span className="text-xs text-slate-400">
          Confidence: {data.confidence.toFixed(0)}%
        </span>
      </div>

      <p className="text-xs text-slate-500 mb-4">
        {data.strategies_matched} of {data.strategies_evaluated} strategies matched
      </p>

      {/* Section 2 — Patterns Checklist */}
      <div className="mb-3">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
          Patterns
        </p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          {best.patterns_fired.map((name) => (
            <div key={`fired-${name}`} className="flex items-start gap-1.5">
              <span className="text-base leading-none mt-0.5">✅</span>
              <div>
                <p className="text-xs font-medium text-white">{name}</p>
                <p className="text-[11px] text-emerald-400/80 leading-tight">
                  {patternNarrative(best, name)}
                </p>
              </div>
            </div>
          ))}
          {best.patterns_missed.map((name) => (
            <div key={`missed-${name}`} className="flex items-start gap-1.5">
              <span className="text-base leading-none mt-0.5 text-slate-600">◻</span>
              <div>
                <p className="text-xs font-medium text-slate-500">{name}</p>
                <p className="text-[11px] text-slate-600 leading-tight">Not confirmed yet</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section 3 — Steps Matched */}
      <p className="text-xs text-slate-400 mt-1 mb-4">Steps matched: {best.steps_matched}</p>

      {/* Section 4 — Contra Assessment */}
      {best.contra_direction && best.contra_narrative && (
        <div className="mb-4 rounded-lg border p-3 bg-slate-800/30 border-slate-700/30">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-amber-400 text-sm">⚠️</span>
            <span className="text-xs font-semibold text-slate-300">Contra Assessment</span>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed mb-2">{best.contra_narrative}</p>
          <div className="flex items-center gap-2">
            <span
              className={`text-xs font-bold ${
                best.contra_direction === 'BUY' ? 'text-emerald-400' : 'text-red-400'
              }`}
            >
              {best.contra_direction}
            </span>
            <span className="text-xs text-slate-500">counter-signal</span>
          </div>
        </div>
      )}

      {/* Section 5 — Convergence Badge */}
      {data.convergence_count >= 2 && (
        <div className="mb-4 rounded-lg border p-3 bg-amber-500/5 border-amber-500/20">
          <p className="text-xs font-semibold text-amber-400 mb-1">
            🔗 {data.convergence_count} strategies converge on {data.direction}
            {data.convergence_boost > 0 && (
              <span className="ml-1 text-amber-300/80">(+{data.convergence_boost}pt boost)</span>
            )}
          </p>
          <p className="text-[11px] text-slate-400">
            {data.convergence_sources.join('  ·  ')}
          </p>
        </div>
      )}

      {/* Section 6 — All-Strategies List */}
      <div className="mt-4">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
          All Strategies
        </p>
        <div className="space-y-1">
          {visibleRows.map((result) => (
            <StrategyBreakdownRow
              key={result.strategy_id}
              result={result}
              isExpanded={expandedRows.has(result.strategy_id)}
              onToggle={() => toggleRow(result.strategy_id)}
            />
          ))}
        </div>
        {!showAll && hiddenCount > 0 && (
          <button
            onClick={() => setShowAll(true)}
            className="mt-2 text-xs text-slate-500 hover:text-slate-300 transition-colors"
          >
            [+ {hiddenCount} more {hiddenCount === 1 ? 'strategy' : 'strategies'}]
          </button>
        )}
        {showAll && hiddenCount > 0 && (
          <button
            onClick={() => setShowAll(false)}
            className="mt-2 text-xs text-slate-500 hover:text-slate-300 transition-colors"
          >
            [show less]
          </button>
        )}
      </div>
    </div>
  );
};

export { StrategySignalCard };
export default StrategySignalCard;
